import { useEffect, useRef, useState } from 'react';

import { Box, Button, CloseButton, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { useLocation, useNavigate } from 'react-router';

import { drawerWidth } from 'components/shared/SideBar';
import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { useCompare } from './CompareContext';
import { CompareDatasetCard, CompareDatasetPlaceholder } from './CompareDatasetCard';
import { buildComparePath } from './compareUrl';
import { MAX_COMPARE_DATASETS } from './constants';
import { datasetRefKey, isSameSystemModel } from './datasetRef';
import { CompareDataset } from './types';
import { useIsDatasetComparisonEnabled } from './useIsDatasetComparisonEnabled';

const IN_PAGE_COLUMN = '@media screen and (min-width: 100em)';

const CompareTray = () => {
  const { datasets, removeDataset, clearDatasets, canCompare, getSelectionAgeSeconds } = useCompare();
  const { ver, system_model } = useVersionInfo();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isEnabled = useIsDatasetComparisonEnabled();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const warningColor = useColorModeValue('orange.600', 'orange.300');

  const isOpen = isEnabled && datasets.length > 0;

  const trayRef = useRef<HTMLElement>(null);
  const [trayHeight, setTrayHeight] = useState(0);

  useEffect(() => {
    const tray = trayRef.current;
    if (!tray || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setTrayHeight(tray.offsetHeight));
    observer.observe(tray);
    return () => observer.disconnect();
  }, [isOpen]);

  if (!isOpen) return null;

  const slots = Array.from({ length: MAX_COMPARE_DATASETS }, (_, index) => datasets[index] ?? null);

  const hasMixedSystemModels = datasets.some((dataset) => !isSameSystemModel(dataset, datasets[0]));

  const trackRemoved = (dataset: CompareDataset, source: 'tray' | 'tray_close', selectionCount: number) =>
    mixpanel.track(MIXPANEL_EVENTS.COMPARE_DATASET_REMOVED, {
      dataset_id: dataset.id,
      dataset_version: dataset.version,
      dataset_system_model: dataset.systemModel,
      source,
      selection_count: selectionCount,
    });

  const onRemove = (dataset: CompareDataset) => {
    trackRemoved(dataset, 'tray', datasets.length - 1);
    removeDataset(dataset);
  };

  const onClose = () => {
    datasets.forEach((dataset, index) => trackRemoved(dataset, 'tray_close', datasets.length - index - 1));
    clearDatasets();
  };

  const onCompare = () => {
    navigate(buildComparePath('impact-assessment', { version: ver, system_model }, datasets), {
      state: { entry: 'tray', secondsToStart: getSelectionAgeSeconds(), from: `${pathname}${search}` },
    });
  };

  return (
    <>
      <Box h={`${trayHeight}px`} flexShrink={0} aria-hidden />
      <Box
        ref={trayRef}
        as="section"
        aria-label="Datasets to compare"
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex="sticky"
        bg={bg}
        borderTopWidth="1px"
        borderColor={borderColor}
        boxShadow="0 -4px 16px rgba(0, 0, 0, 0.08)"
      >
        <CloseButton position="absolute" top={2} right={2} size="sm" onClick={onClose} aria-label="Clear comparison" />
        <Box sx={{ [IN_PAGE_COLUMN]: { pl: `${drawerWidth}px` } }}>
          <Box px={{ base: 4, md: 6, '2xl': 12 }} sx={{ [IN_PAGE_COLUMN]: { px: 5 } }}>
            <Stack
              sx={{ [IN_PAGE_COLUMN]: { maxW: 'container.xl', mx: 'auto' } }}
              pt={{ base: 10, lg: 6 }}
              pb={{ base: 4, lg: 6 }}
              direction={{ base: 'column', lg: 'row' }}
              align={{ base: 'stretch', lg: 'center' }}
              spacing={6}
            >
              <Stack direction={{ base: 'column', md: 'row' }} flex={1} align="stretch" spacing={{ base: 3, md: 6 }} minW={0}>
                {slots.map((dataset, index) =>
                  dataset ? (
                    <CompareDatasetCard
                      key={datasetRefKey(dataset)}
                      dataset={dataset}
                      onRemove={() => onRemove(dataset)}
                      isCompactOnMobile
                    />
                  ) : (
                    <CompareDatasetPlaceholder key={`placeholder-${index}`} index={index + 1} />
                  )
                )}
              </Stack>
              <Stack spacing={2} align={{ base: 'stretch', lg: 'center' }} alignSelf={{ base: 'stretch', lg: 'center' }}>
                <Button
                  colorScheme="green"
                  size="lg"
                  minW={{ lg: '190px' }}
                  isDisabled={!canCompare || hasMixedSystemModels}
                  onClick={onCompare}
                >
                  Compare
                </Button>
                {hasMixedSystemModels && (
                  <Text fontSize="xs" color={warningColor} maxW="220px" textAlign={{ base: 'left', lg: 'center' }}>
                    Datasets must share a system model to be compared.
                  </Text>
                )}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CompareTray;
